/**
 * Display and error helpers for the Supabase OAuth 2.1 server surfaces:
 * the consent page and Settings > Connected apps (ADR-058).
 *
 * Everything a client shows about itself — name, website, redirect URI —
 * is chosen by whoever registered it, and registration is open (DCR), so
 * it is rendered as inert text, bounded, and never as a link or image.
 */

const MAX_LABEL_LENGTH = 80;

// Bidi overrides/isolates and other invisible formatting characters that
// can make a name read differently from what it is ("Kopai" rendered
// from reversed text, a trailing host hidden after an override). Built
// from code points so the source itself stays free of invisible text.
const INVISIBLE_RANGES: [number, number][] = [
  [0x0000, 0x001f], // C0 controls
  [0x007f, 0x007f], // DEL
  [0x200b, 0x200f], // zero-width space/joiners, LRM, RLM
  [0x202a, 0x202e], // bidi embeddings and overrides
  [0x2060, 0x2069], // word joiner, bidi isolates
  [0xfeff, 0xfeff], // zero-width no-break space / BOM
];
const INVISIBLE_FORMATTING = new RegExp(
  `[${INVISIBLE_RANGES.map(
    ([from, to]) => `\\u{${from.toString(16)}}-\\u{${to.toString(16)}}`,
  ).join("")}]`,
  "gu",
);

/** A client-declared label, stripped of formatting characters and capped. */
export function clientLabel(name: string | null | undefined): string {
  const cleaned = (name ?? "").replace(INVISIBLE_FORMATTING, "").trim();
  if (!cleaned) return "Unnamed app";
  return cleaned.length > MAX_LABEL_LENGTH
    ? `${cleaned.slice(0, MAX_LABEL_LENGTH - 1)}…`
    : cleaned;
}

/**
 * The part of a URI a user can check: `URL.host` for http(s), which keeps
 * the port (`localhost:54545` for a loopback callback) and stays in
 * punycode for internationalised names, so a look-alike host shows as
 * `xn--…` rather than as the brand it imitates. Other schemes (native app
 * callbacks) show as the scheme plus whatever follows it, capped.
 * Returns null when there is nothing meaningful to show.
 */
export function uriHost(uri: string | null | undefined): string | null {
  if (!uri) return null;
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return null;
  }
  if (url.protocol === "http:" || url.protocol === "https:") {
    return url.host || null;
  }
  return clientLabel(`${url.protocol}${url.host || url.pathname}`);
}

/**
 * Whether a consent page's `?authorization_id=` has the shape GoTrue
 * gives it (32 alphanumerics, v2.196.0; the bounds leave room for a
 * change). supabase-js puts the id into the request path unencoded, so a
 * value carrying `/`, `.`, `%`, `?` or `&` could point the page's
 * authenticated request at another Auth endpoint; nothing like that gets
 * as far as a request, or through the login round trip.
 */
export function isAuthorizationId(value: string | null): value is string {
  return value !== null && /^[A-Za-z0-9]{16,64}$/.test(value);
}

// Schemes GoTrue itself refuses as redirect URIs: navigating to one runs
// code in, or hands content to, the portal's origin.
const DANGEROUS_SCHEMES = new Set([
  "javascript:",
  "data:",
  "vbscript:",
  "blob:",
  "file:",
  "about:",
]);

/**
 * Whether a `redirect_url` from the consent API is safe to navigate to:
 * an absolute URL whose scheme is not one of the above. Native-app
 * callbacks (custom schemes) and loopback http stay allowed.
 */
export function isSafeRedirectUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return !DANGEROUS_SCHEMES.has(parsed.protocol);
}

/**
 * Leaves the portal for the client's redirect URI, replacing the consent
 * page in history so Back does not return to a request already decided.
 * Refuses, and returns false, for a URL that is not safe to follow.
 */
export function leaveTo(url: string): boolean {
  if (!isSafeRedirectUrl(url)) return false;
  window.location.replace(url);
  return true;
}

/** First eight characters of a client id, for telling look-alike rows apart. */
export function shortClientId(clientId: string): string {
  return clientId.slice(0, 8);
}

/**
 * Whether an error from the OAuth consent API is worth retrying.
 *
 * GoTrue (v2.196.0, verified locally) answers 404
 * `oauth_authorization_not_found` for an unknown id or one already bound
 * to another user — an authorization belongs to the first user who reads
 * it — and 400 `validation_failed` for one that is expired or already
 * decided. None of those can succeed on retry; a network failure, a 5xx
 * or a rate limit (429) can.
 */
export function isRetryableOAuthError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return true;
  const status = (error as { status?: unknown }).status;
  if (typeof status !== "number" || status === 0) return true;
  return status >= 500 || status === 429;
}

const SIGN_IN_AGAIN_CODES = new Set([
  "bad_jwt",
  "no_authorization",
  "session_not_found",
  "session_expired",
]);

/**
 * Whether an error from the OAuth consent API means the portal session is
 * gone or no longer accepted, so signing in again (as the same user) can
 * still pick the request up. supabase-js reports a missing local session
 * as `AuthSessionMissingError` (status 400), which the status alone would
 * misread as a terminal validation failure.
 */
export function needsSignInAgain(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { name, status, code } = error as {
    name?: unknown;
    status?: unknown;
    code?: unknown;
  };
  if (name === "AuthSessionMissingError") return true;
  if (status === 401) return true;
  return typeof code === "string" && SIGN_IN_AGAIN_CODES.has(code);
}
